<template>
  <Teleport to="body">
    <div
      v-if="open"
      class="relative z-[100080]"
      role="dialog"
      aria-modal="true"
      aria-labelledby="cue-hold-title"
    >
      <div
        class="fixed inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity animate__animated animate__fadeIn"
        style="--animate-duration: 0.2s"
        aria-hidden="true"
      />

      <div class="fixed inset-0 z-10 overflow-y-auto pointer-events-none">
        <div class="flex min-h-full items-center justify-center p-6">
          <div
            class="pointer-events-auto w-full max-w-md overflow-hidden rounded-2xl border border-teal-100 bg-white text-left shadow-2xl ring-1 ring-teal-200/60 animate__animated animate__fadeInUp"
            style="--animate-duration: 0.28s"
            @click.stop
          >
            <div class="bg-gradient-to-r from-teal-600 to-teal-500 px-6 py-5 text-white">
              <div class="flex items-start gap-4">
                <span
                  class="relative flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-white/15 ring-1 ring-white/25"
                  aria-hidden="true"
                >
                  <font-awesome-icon :icon="copy.icon" class="h-5 w-5 text-white" />
                </span>
                <div class="min-w-0 flex-1">
                  <p class="text-[11px] font-semibold uppercase tracking-[0.14em] text-teal-100">
                    {{ copy.eyebrow }}
                  </p>
                  <h2 id="cue-hold-title" class="mt-1 text-lg font-bold leading-tight tracking-tight">
                    {{ copy.title }}
                  </h2>
                  <p v-if="courseName" class="mt-1 truncate text-sm text-teal-50/90">
                    {{ courseName }}
                  </p>
                </div>
              </div>
            </div>

            <div class="space-y-4 px-6 py-5">
              <p class="text-sm leading-relaxed text-gray-600">
                {{ copy.lead }}
              </p>

              <p
                class="flex items-start gap-2.5 rounded-xl border border-amber-200/80 bg-amber-50 px-3.5 py-3 text-sm leading-relaxed text-amber-950"
              >
                <font-awesome-icon
                  icon="fa-solid fa-triangle-exclamation"
                  class="mt-0.5 shrink-0 text-amber-500"
                  aria-hidden="true"
                />
                <span>{{ copy.continueWarning }}</span>
              </p>

              <div
                v-if="copy.whyBody"
                class="rounded-xl border border-teal-100 bg-gradient-to-r from-teal-50/90 to-white px-3.5 py-3"
              >
                <p class="text-sm font-semibold text-teal-900">{{ copy.whyTitle }}</p>
                <p class="mt-1 text-sm leading-relaxed text-slate-600">{{ copy.whyBody }}</p>
              </div>
            </div>

            <div class="flex flex-col gap-3 border-t border-gray-100 bg-gray-50/90 px-6 py-4 sm:flex-row sm:justify-end">
              <button
                type="button"
                class="inline-flex items-center justify-center rounded-full border border-gray-300 bg-white px-5 py-2.5 text-sm font-semibold text-gray-700 shadow-sm transition hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:ring-offset-2"
                @click="$emit('cancel')"
              >
                {{ copy.waitLabel }}
              </button>
              <button
                type="button"
                class="inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-teal-500 to-teal-600 px-5 py-2.5 text-sm font-bold text-white shadow-md transition hover:from-teal-600 hover:to-teal-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:ring-offset-2"
                @click="$emit('confirm')"
              >
                {{ copy.continueLabel }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script>
import {
  buildHoldOnMessage,
  getCourseUpdateExpectations,
  normalizeHoldOnAction,
} from '@/utils/courseUpdateExpectations'

export default {
  name: 'CourseUpdateHoldOnModal',
  props: {
    open: { type: Boolean, default: false },
    course: { type: Object, default: null },
    endDate: { type: String, default: '' },
    /** @values take, buy, assign, add */
    action: { type: String, default: 'assign' },
  },
  emits: ['cancel', 'confirm'],
  computed: {
    resolveEnd() {
      if (this.endDate) return this.endDate
      return getCourseUpdateExpectations(this.course).updateWindowEnd
    },
    copy() {
      return buildHoldOnMessage(this.resolveEnd, normalizeHoldOnAction(this.action))
    },
    courseName() {
      return String(this.course?.name || this.course?.title || '').trim()
    },
  },
}
</script>
